import { Canonical } from '../hashState';
import { Rng, RngState } from '../Rng';
import { advanceTimer } from '../SimClock';
import { HouseManager } from './HouseManager';
import { IndustryType, INDUSTRY_CONFIGS, ALL_INDUSTRIES } from './EconomyTypes';

/**
 * How much of what the treasury would like to spend it may.
 *
 * Declared here rather than imported, so that this class stays unable to name a
 * tithe, a power bill or a covenant. It knows what it received this cycle and
 * what its policy would cost; something else decides how much of that receipts
 * can carry. `FiscalManager` is what satisfies it (see `SpendingLimit` there),
 * and `SurfaceSim` is what ties the two together.
 *
 * Absent, the treasury spends freely, which is what it has always done and what
 * every test that constructs an `EconomyManager` alone still gets.
 */
export interface CycleBudget {
  ration(receipts: number, want: number): number;
}

export interface EconomyConfig {
  UBI_INTERVAL: number;
  UBI_AMOUNT: number;
  TAX_RATE: number;
  DIVIDEND_RATE: number;
  INFRA_BUDGET: number;
  BUSINESS_GRANT: number;
  /**
   * Interest charged on outstanding debt, per budget cycle.
   *
   * **No longer a local number.** §8 says debt interest *"stops being a local
   * slider and becomes a World Market rate"*, and `MarketManager` posts it: this
   * value is what an unposted district borrows at, and `SurfaceSim` overwrites it
   * from the market at every budget cycle. Editing it in a settings panel changes
   * the world for as long as it takes the next cycle to settle.
   *
   * The default was `0.02` from before there were seasons, which compounds to
   * 494% over the ninety cycles of one (ADR-024 D160). It is `MarketConfig`'s
   * shipped `BOND_YIELD` now, and the two must agree or a district's first cycle
   * would be charged at a rate nobody posted.
   */
  INTEREST_RATE: number;
}

export const DEFAULT_ECONOMY_CONFIG: EconomyConfig = {
  UBI_INTERVAL: 10000,
  UBI_AMOUNT: 30,
  TAX_RATE: 0.25,
  DIVIDEND_RATE: 0.5,
  INFRA_BUDGET: 50,
  BUSINESS_GRANT: 100,
  INTEREST_RATE: 0.0005,
};

export interface BudgetSnapshot {
  governmentPool: number;
  lastCycleRevenue: number;
  /** Tax, rent and toll: what the rationing rule was allowed to spend. */
  lastCycleReceipts: number;
  lastCycleSpending: number;
  lastCycleNet: number;
  lastCycleInterest: number;
  roadQuality: number;
  familyCount: number;
  avgFamilyCash: number;
  corpCount: number;
}

/**
 * The treasury's complete state. See `EconomyManager.save()`.
 *
 * Every field required. A snapshot is written by this version of the code and
 * read by this version of the code, so a missing field is a bug rather than an
 * old save — and since ADR-006 there are no old saves, by decision (ADR-005 D22).
 */
export interface EconomySnapshot {
  governmentPool: number;
  totalTaxCollected: number;
  totalLeaseCollected: number;
  totalTollsCollected: number;
  totalRoadWear: number;
  totalInterchangeSpending: number;
  totalUtilityBilled: number;
  totalTitheRemitted: number;
  totalGrantsReceived: number;
  totalWorksSpending: number;
  totalUBIDistributed: number;
  totalDividendsDistributed: number;
  roadQuality: number;
  totalBusinessGrantsPaid: number;
  totalInfraSpending: number;
  totalInterestPaid: number;
  ubiTimer: number;
  recurringTimer: number;
  leaseMark: number;
  tollMark: number;
  lastCycleRevenue: number;
  lastCycleReceipts: number;
  lastCycleSpending: number;
  lastCycleInterest: number;
  cyclesSettled: number;
  paymentFailuresThisCycle: number;
  failureStatsMode: boolean;
  spendingConstrained: boolean;
  rng: RngState;
}

const INITIAL_FAMILY_CASH = 100;
const INITIAL_GOV_POOL = 1000;
const RECURRING_CHECK_INTERVAL = 1000;

/**
 * Numeric keys of a record, ascending.
 *
 * Anywhere an iteration over object keys can change state, the order has to be
 * a promise rather than an engine behaviour. See `docs/determinism.md` rule 4.
 */
function sortedNumericKeys(record: Record<number, number>): number[] {
  return Object.keys(record).map(Number).sort((a, b) => a - b);
}

/**
 * Manages the government budget economy: taxes, UBI, infrastructure, business grants.
 *
 * The government spends freely on its own households; debt accrues interest, which
 * is the real constraint on that. What it buys from *outside* the district — power,
 * water, a levy, a covenant — is rationed against the cycle's receipts, and the
 * road budget is what is left over. See `CycleBudget` and `FiscalManager`.
 */
export class EconomyManager {
  private governmentPool = INITIAL_GOV_POOL;
  private totalTaxCollected = 0;
  /**
   * Rent the federal estate has paid this district, over its whole life.
   *
   * The one revenue line the district's own economy did not generate. §8 lists
   * leases under the district budget, so it is counted here beside the tax rather
   * than in the estate that pays it — a treasury should be able to say where its
   * money came from without asking a landlord.
   */
  private totalLeaseCollected = 0;
  /**
   * Tolls transit freight has paid this district, over its whole life.
   *
   * The second revenue line the district's own economy did not generate, and the
   * first one it had to build something to get. Counted apart from the rent because
   * the two are opposite bargains: rent is paid for ground that was taken, and a
   * toll is paid for a road the mayor laid on purpose.
   */
  private totalTollsCollected = 0;
  /**
   * Points of road quality transit has worn away, over the district's whole life.
   *
   * Not money, and the only lifetime total here that is not. It is kept beside the
   * dollars because it is the other half of the same transaction: `receiveTolls`
   * and `wearRoads` are called in the same breath on the same legs, and a reader
   * comparing them is asking whether a corridor paid for itself.
   */
  private totalRoadWear = 0;
  /**
   * Dollars this district has spent on interchanges, lifetime.
   *
   * Capital spending on the district's own road network, and the only line here a
   * mayor buys expecting a return. See `payForInterchange` for why it is not
   * `totalWorksSpending`.
   */
  private totalInterchangeSpending = 0;
  /**
   * Dollars this district has paid for the power and water it was delivered.
   *
   * §8's *"Power & water bills | − | Metered at polity prices"*, and the first
   * outflow in this treasury that scales with how much town there is: every home
   * built, every shop opened and every cell of street lighting adds to it.
   *
   * Priced by `FiscalManager` against what the meters delivered, so a district the
   * grid shed pays for nothing it did not get. This class is told an amount and
   * charges it; it has never heard of a kilowatt.
   */
  private totalUtilityBilled = 0;
  /**
   * Dollars remitted upward, lifetime. §8's extraction dial, as a total.
   *
   * Levied once a season on the season's own tax revenue, at a rate a governor
   * sets. Zero in a district nobody has addressed, which is every district in this
   * repository.
   */
  private totalTitheRemitted = 0;
  /**
   * Dollars received from above, lifetime.
   *
   * The other half of the same dial: §9's *"a season of negative tithe — grants
   * flow down"*. Counted separately from the tithe rather than netted against it,
   * because a district that was taxed 22% and later given 22% back is not a
   * district that was left alone, and a public record should be able to say so.
   */
  private totalGrantsReceived = 0;
  /**
   * Dollars this district has committed to works of its own that yield nothing.
   *
   * §10's mercy builds — a covenant, a hedgerow, an undercrossing — and the first
   * spending line in this treasury the *mayor* decides. UBI, infrastructure and
   * business grants are all standing policy: they happen every cycle at a rate from
   * the config, and no click causes one. This one arrives a verb at a time.
   *
   * Counted separately from `totalInfraSpending` for that reason and not because it
   * is nobler. Infrastructure buys road quality, which buys car speed and a
   * quality-of-life term (ADR-019 D120); this buys nothing measurable, which is the
   * fact a Chronicle should be able to state about it rather than have averaged into
   * a public-works total.
   */
  private totalWorksSpending = 0;
  private totalUBIDistributed = 0;
  private totalDividendsDistributed = 0;

  // Budget tracking
  private roadQuality = 50;
  private lastCycleRevenue = 0;
  /**
   * What actually came in last cycle: tax plus whatever rent and toll arrived
   * with it.
   *
   * Distinct from `lastCycleRevenue`, which is the district's own economy and is
   * what the panel has always shown. This is what the rationing rule is allowed to
   * spend, and rent from a machine nobody asked for spends exactly as well as tax.
   */
  private lastCycleReceipts = 0;
  private lastCycleSpending = 0;
  private lastCycleInterest = 0;
  private totalBusinessGrantsPaid = 0;
  private totalInfraSpending = 0;
  private totalInterestPaid = 0;
  private cachedSnapshot: BudgetSnapshot | null = null;

  private ubiTimer = 0;
  private recurringTimer = 0;

  /**
   * Lifetime rent already counted as receipts.
   *
   * The estate pays after the cycle settles (`SurfaceSim.payLease`), so a cycle's
   * receipts include the rent that arrived during the previous one. A mark rather
   * than an accumulator, for the reason `FiscalManager`'s three marks are: the
   * lifetime total is owned in one place and a subtraction against it cannot
   * disagree with it.
   */
  private leaseMark = 0;

  /**
   * Lifetime tolls already counted as receipts.
   *
   * `leaseMark`'s twin, for the same reason and with the same timing: freight is
   * settled after the cycle closes (`SurfaceSim.payTolls`), so a cycle spends the
   * tolls of the crossing before it.
   */
  private tollMark = 0;

  /**
   * Whether the last settled cycle could not fund the road budget in full.
   *
   * Held so the ticker says it once when it starts rather than ninety times a
   * season. State, and snapshotted: a district restored mid-austerity would
   * otherwise announce it again.
   */
  private spendingConstrained = false;

  /**
   * Budget cycles settled since the world began.
   *
   * The cadence-invariance the tick rate depends on is exactly this number: over
   * a span of sim-time, a district must settle the same number of cycles at 6 Hz
   * as at 60. Counting it is cheaper than inferring it from the treasury, and
   * Phase 2's season settlement wants a cycle count anyway (a season is 90 of
   * them).
   */
  private cyclesSettled = 0;
  private paymentFailuresThisCycle = 0;
  private failureStatsMode = false;

  private config: EconomyConfig;
  private rng: Rng;

  constructor(rng: Rng, config: Partial<EconomyConfig> = {}) {
    this.rng = rng;
    this.config = { ...DEFAULT_ECONOMY_CONFIG, ...config };
  }

  getConfig(): EconomyConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<EconomyConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  getGovernmentPool(): number {
    return this.governmentPool;
  }

  getTotalTaxCollected(): number {
    return this.totalTaxCollected;
  }

  /** Rent the federal estate has paid, lifetime. Zero without one on the map. */
  getTotalLeaseCollected(): number {
    return this.totalLeaseCollected;
  }

  /**
   * Take a lease payment from the federal estate.
   *
   * Called once per settled budget cycle by `SurfaceSim`, which is the only
   * object that can see both a landlord and a tenant. It is deliberately *not*
   * folded into `lastCycleRevenue`: that figure is what the district's own
   * economy produced, and rent from a machine nobody asked for is not that.
   */
  receiveLease(amount: number): void {
    if (!(amount > 0)) return;
    this.governmentPool += amount;
    this.totalLeaseCollected += amount;
    this.cachedSnapshot = null;
  }

  /** Tolls transit freight has paid this district, lifetime. Zero off a corridor. */
  getTotalTollsCollected(): number {
    return this.totalTollsCollected;
  }

  /**
   * Take a cycle's tolls from the freight that crossed the district.
   *
   * §6's revenue split, fourth stream: *"Tolls (if Charter allows) | Host districts
   * along freight routes | Per truck-leg."* Called once per settled budget cycle by
   * `SurfaceSim`, which is the only object that can see both a corridor and a
   * treasury.
   *
   * Receipts, like the rent and unlike the tax: it is money from outside the
   * district, so it funds the road budget under ADR-021's rule. Which is the whole
   * arithmetic of a corridor — the trucks pay for the roads they are wearing out,
   * and whether they pay for enough of them is `TOLL_PER_LEG` against
   * `WEAR_PER_LEG` and is a question the mayor's road quality answers.
   */
  receiveTolls(amount: number): void {
    if (!(amount > 0)) return;
    this.governmentPool += amount;
    this.totalTollsCollected += amount;
    this.cachedSnapshot = null;
  }

  /** Points of road quality worn away by traffic the district did not generate. */
  getTotalRoadWear(): number {
    return this.totalRoadWear;
  }

  /**
   * Take points off the road for traffic the maintenance budget did not plan for.
   *
   * §5's *"road wear"*, and the only quantity in this class that is not money. The
   * treasury already decays the road by three points a cycle and buys them back
   * with `applyInfrastructure`; this is the same subtraction with somebody else's
   * trucks behind it, applied *after* the cycle's maintenance so that a road
   * repaired to 100 and then crossed shows the crossing.
   *
   * Deliberately not folded into the maintenance formula. A district's own decay is
   * a function of how much it has built, which is its own business; this is a
   * function of how much freight the polity above routed through it, which is not,
   * and a mayor should be able to point at the two figures separately.
   */
  wearRoads(points: number): void {
    if (!(points > 0)) return;
    this.roadQuality = Math.max(0, this.roadQuality - points);
    this.totalRoadWear += points;
    this.cachedSnapshot = null;
  }

  /** Dollars the district has spent building interchanges, lifetime. */
  getTotalInterchangeSpending(): number {
    return this.totalInterchangeSpending;
  }

  /**
   * Pay for an interchange.
   *
   * Its own line rather than `payForWorks`, which is §10's works and is documented
   * as the money a district gets nothing for. An interchange is the opposite trade
   * — it is bought precisely because it pays — and a Chronicle in which the two
   * shared a total would be one that could not say whether a mayor had been
   * generous or entrepreneurial.
   *
   * Unconditional, like every other payment here, and committed against receipts in
   * the same breath (`SurfaceSim.buildInterchange`): a corridor bought behind a levy
   * costs a month of road maintenance before it has carried anything.
   */
  payForInterchange(amount: number): void {
    if (!(amount > 0)) return;
    this.governmentPool -= amount;
    this.totalInterchangeSpending += amount;
    this.cachedSnapshot = null;
  }

  /** What the district has paid for delivered power and water, lifetime. */
  getTotalUtilityBilled(): number {
    return this.totalUtilityBilled;
  }

  /**
   * Pay a cycle's utility bill.
   *
   * Unconditional, like every other payment here, and this one could not sensibly
   * be otherwise: the kilowatts were delivered before the invoice existed. What
   * a district cannot afford it borrows for, and what it borrows shows up as a
   * claim on next cycle's road budget rather than as a refusal here.
   */
  payUtilities(amount: number): void {
    if (!(amount > 0)) return;
    this.governmentPool -= amount;
    this.totalUtilityBilled += amount;
    this.cachedSnapshot = null;
  }

  /** Dollars remitted upward, lifetime. Zero without a governor. */
  getTotalTitheRemitted(): number {
    return this.totalTitheRemitted;
  }

  /** Dollars received from above, lifetime. Zero without a generous one. */
  getTotalGrantsReceived(): number {
    return this.totalGrantsReceived;
  }

  /**
   * Settle a season's tithe. A negative amount is a grant coming down.
   *
   * One call for both directions because it is one dial (§9: *"Tithes to zero,
   * then a season of negative tithe — grants flow down"*), and two counters
   * because the record has to be able to distinguish a district that was left
   * alone from one that was taxed and repaid.
   */
  remitTithe(amount: number): void {
    if (!Number.isFinite(amount) || amount === 0) return;
    this.governmentPool -= amount;
    if (amount > 0) this.totalTitheRemitted += amount;
    else this.totalGrantsReceived += -amount;
    this.cachedSnapshot = null;
  }

  /** What the district has spent on works that pay nothing. Zero in most of them. */
  getTotalWorksSpending(): number {
    return this.totalWorksSpending;
  }

  /**
   * Pay for a mercy build.
   *
   * Still unconditional: there is no affordability check here and mercy is not
   * where the first one belongs. A mayor who deeds a meadow they cannot afford has
   * borrowed for it, at `INTEREST_RATE` a cycle, compounding, forever.
   *
   * What changed in ADR-021 is what the borrowing *does*. A covenant is a purchase
   * from outside the district, so `SurfaceSim` commits it to the fiscal account in
   * the same breath, and the road budget gets nothing until receipts have covered
   * it. Buy one in a good season and it is absorbed in three cycles; buy one behind
   * a 22% tithe and the roads are unfunded for a month. Nothing refuses the click,
   * and the potholes arrive on their own.
   */
  payForWorks(amount: number): void {
    if (!(amount > 0)) return;
    this.governmentPool -= amount;
    this.totalWorksSpending += amount;
    this.cachedSnapshot = null;
  }

  getTotalUBIDistributed(): number {
    return this.totalUBIDistributed;
  }

  getTotalDividendsDistributed(): number {
    return this.totalDividendsDistributed;
  }

  getRoadQuality(): number {
    return this.roadQuality;
  }

  // The three lifetime totals below have always been tracked and never been
  // readable. A season record is a set of monotone sums (`SeasonManager`), and
  // the spending side of the ledger is half of it.

  getTotalBusinessGrantsPaid(): number {
    return this.totalBusinessGrantsPaid;
  }

  getTotalInfraSpending(): number {
    return this.totalInfraSpending;
  }

  getTotalInterestPaid(): number {
    return this.totalInterestPaid;
  }

  /** Budget cycles settled since the world began. See `cyclesSettled`. */
  getCyclesSettled(): number {
    return this.cyclesSettled;
  }

  /**
   * Road quality affects car speed: 0.6 at quality=0, 1.0 at quality=100.
   */
  getRoadSpeedMultiplier(): number {
    return 0.6 + 0.4 * (this.roadQuality / 100);
  }

  /**
   * Get cached budget snapshot (computed once per cycle, cheap to call per frame).
   */
  getBudgetSnapshot(houses: HouseManager): BudgetSnapshot {
    if (this.cachedSnapshot) return this.cachedSnapshot;

    const families = houses.getAllFamilies();
    const avgCash = families.length > 0
      ? families.reduce((sum, f) => sum + f.cash, 0) / families.length
      : 0;
    const corpCount = houses.getHouses().filter(h => h.occupant === 'corporation' && h.built).length;

    this.cachedSnapshot = {
      governmentPool: this.governmentPool,
      lastCycleRevenue: this.lastCycleRevenue,
      lastCycleReceipts: this.lastCycleReceipts,
      lastCycleSpending: this.lastCycleSpending,
      lastCycleNet: this.lastCycleRevenue - this.lastCycleSpending,
      lastCycleInterest: this.lastCycleInterest,
      roadQuality: this.roadQuality,
      familyCount: families.length,
      avgFamilyCash: avgCash,
      corpCount,
    };
    return this.cachedSnapshot;
  }

  /**
   * Pay a business grant for a new corporation.
   * Always pays — debt consequences handled by interest mechanics.
   */
  payBusinessGrant(): void {
    const grantAmount = this.config.BUSINESS_GRANT;
    if (grantAmount <= 0) return;

    this.governmentPool -= grantAmount;
    this.totalBusinessGrantsPaid += grantAmount;
  }

  /**
   * Called each frame from ParkingtonLayer.update().
   *
   * `budget` is §8's rationing rule and is optional: without one the treasury
   * spends freely, which is what it did for the whole of this game's life before
   * there was anything above the district to owe money to.
   *
   * `roads` says whether the district has any pavement to maintain. It defaults
   * to `true`, which is what every caller before ADR-025 meant, and a district
   * that answers `false` neither spends its maintenance budget nor decays — see
   * `applyInfrastructure`.
   */
  update(dt: number, houses: HouseManager, budget?: CycleBudget, roads = true): void {
    // UBI + Tax + Dividend cycle.
    //
    // The accumulator carries its overshoot instead of being zeroed. Zeroing
    // rounds the budget cycle up to a whole tick and drifts late — invisible at
    // 60 Hz, a 17% error at a cadence where 10,000 ms does not land on a tick
    // boundary. A budget cycle that runs slow is a tax cut nobody voted for.
    // Same fix, same reason, as `MetabolismSystem` in ADR-002 §3.
    const ubi = advanceTimer(this.ubiTimer, dt, this.config.UBI_INTERVAL, 1);
    this.ubiTimer = ubi.value;
    if (ubi.fires > 0) {
      this.cyclesSettled += ubi.fires;
      // Emit failure summary from previous cycle if in stats mode
      if (this.failureStatsMode && this.paymentFailuresThisCycle > 0) {
        houses.addNews(`${this.paymentFailuresThisCycle} families couldn't pay bills this cycle`, 'economy');
      }
      // Switch modes based on failure count
      if (this.paymentFailuresThisCycle > 3) {
        this.failureStatsMode = true;
      } else if (this.paymentFailuresThisCycle <= 1) {
        this.failureStatsMode = false;
      }
      this.paymentFailuresThisCycle = 0;

      // === Budget cycle: collect → interest → spend ===
      const cycleRevenue = this.collectTaxes(houses);
      this.lastCycleRevenue = cycleRevenue;

      // Interest accrues on outstanding debt (the real constraint on borrowing)
      const interest = this.accrueInterest();
      this.lastCycleInterest = interest;

      // What actually came in: the district's own tax, plus whatever rent the
      // estate paid and toll the freight paid since the last cycle settled. Both
      // arrive after the cycle closes (`SurfaceSim.payLease`, `payTolls`), so a
      // cycle spends the previous cycle's.
      const receipts = cycleRevenue
        + Math.max(0, this.totalLeaseCollected - this.leaseMark)
        + Math.max(0, this.totalTollsCollected - this.tollMark);
      this.leaseMark = this.totalLeaseCollected;
      this.tollMark = this.totalTollsCollected;
      this.lastCycleReceipts = receipts;

      // Spend. UBI is unconditional: it is a transfer to the district's own
      // households and the closed loop returns it as tax on what they spend, so a
      // district can run it at a deficit against itself indefinitely — that
      // deficit is what its money supply is. Road maintenance is a purchase from
      // outside the district, and §8 is explicit about which one gives way when
      // there is a levy to pay: *"cut infrastructure (roads decay, commerce slows
      // — the existing death-spiral)"*. See `FiscalManager`.
      const families = houses.getAllFamilies();
      this.distributeUBI(houses, this.config.UBI_AMOUNT);
      const wanted = roads ? this.config.INFRA_BUDGET : 0;
      const infra = budget ? budget.ration(receipts, wanted) : wanted;
      this.applyInfrastructure(infra, houses, roads);
      this.reportConstraint(houses, infra, wanted);

      this.lastCycleSpending = this.config.UBI_AMOUNT * families.length
        + infra + interest;

      // Invalidate cached snapshot
      this.cachedSnapshot = null;

      // Interest warning when debt servicing is significant
      if (interest > 0 && cycleRevenue > 0) {
        const interestShare = interest / cycleRevenue;
        if (interestShare > 0.5) {
          houses.addNews(`Debt interest ($${Math.floor(interest)}) consuming ${Math.round(interestShare * 100)}% of revenue`, 'economy');
        }
      }

      this.distributeDividends(houses);
      // Hardship used to be checked here, between the dividend and the vacancy.
      // It is `WelfareManager` now (ADR-018): the treasury decides what a
      // household is *paid* and no longer decides whether it stays, because the
      // reasons a family leaves stopped being fiscal the day the grid could put
      // their lights out. The measure settles at the end of the same tick, after
      // both meters and the payroll — so a family who leaves this cycle vacates
      // a house that this line has already passed over, and it is refilled next
      // cycle rather than the same one.
      // Filling a vacant home used to happen here too. It is `SurfaceSim`'s now
      // (ADR-019): whether anybody would move in depends on the grid, the mains
      // and the district's quality of life, and the treasury can see none of the
      // three.
      this.familySharePurchases(houses);
    }

    // Recurring payments check
    const recurring = advanceTimer(this.recurringTimer, dt, RECURRING_CHECK_INTERVAL, 1);
    this.recurringTimer = recurring.value;
    if (recurring.fires > 0) {
      this.processRecurringPayments(houses);
    }
  }

  /**
   * Say once, when it starts, that the roads are not being maintained.
   *
   * The ticker's line about austerity, and it is deliberately about the *road*
   * rather than about the levy that caused it. A mayor watching the panel sees
   * potholes and a suspended budget line; which of the bills, the tithe or their
   * own last covenant is responsible is arithmetic they can do, and this sentence
   * does not do it for them. Same register as *"Roads deteriorating"* below, which
   * has been the district's voice about its own decay since long before there was
   * anybody to blame for it.
   */
  private reportConstraint(houses: HouseManager, infra: number, wanted: number): void {
    const short = infra < wanted;
    if (short && !this.spendingConstrained) {
      houses.addNews(
        `Road budget suspended — $${Math.round(wanted - infra)} unfunded this cycle`,
        'economy'
      );
    }
    this.spendingConstrained = short;
  }

  /**
   * Accrue interest on outstanding government debt.
   * Returns the interest amount paid this cycle.
   */
  private accrueInterest(): number {
    if (this.governmentPool >= 0 || this.config.INTEREST_RATE <= 0) return 0;
    const interest = Math.abs(this.governmentPool) * this.config.INTEREST_RATE;
    this.governmentPool -= interest;
    this.totalInterestPaid += interest;
    return interest;
  }

  /**
   * Distribute UBI to all families with the given per-family amount.
   */
  private distributeUBI(houses: HouseManager, amountPerFamily: number): void {
    if (amountPerFamily <= 0) return;
    const families = houses.getAllFamilies();
    for (const family of families) {
      houses.addFamilyCash(family.id, amountPerFamily);
      this.governmentPool -= amountPerFamily;
      this.totalUBIDistributed += amountPerFamily;
    }
  }

  /**
   * Collect taxes from all corporations. Returns total collected this cycle.
   */
  private collectTaxes(houses: HouseManager): number {
    let cycleRevenue = 0;
    const allHouses = houses.getHouses();
    for (const house of allHouses) {
      if (house.occupant !== 'corporation') continue;
      const revenue = houses.getHouseRevenue(house.id);
      if (revenue <= 0) continue;

      const tax = revenue * this.config.TAX_RATE;
      houses.deductHouseRevenue(house.id, tax);
      houses.addHouseTaxesPaid(house.id, tax);
      this.governmentPool += tax;
      this.totalTaxCollected += tax;
      cycleRevenue += tax;
    }
    return cycleRevenue;
  }

  /**
   * Apply infrastructure spending: updates road quality.
   * maintenanceCost scales with development: 20 + builtBuildingCount * 3
   *
   * **Nothing happens on a district with no pavement**, which is ADR-024 Q59 and
   * the one figure in a `SeasonRecord` that a reviewer could not walk back to an
   * event: an untouched meadow no player had ever visited spent $4,500 a season
   * maintaining roads that did not exist, borrowed it, and compounded interest on
   * the borrowing. `INFRA_BUDGET` was spent every cycle unconditionally.
   *
   * The decay stops too, and that is the part worth arguing. A road at 100 in a
   * district with no roads is not a claim that the pavement is immaculate — there
   * is no pavement, and `roadQuality` is a property of a thing that does not
   * exist. The first cell poured starts both the spending and the decay together,
   * which is the only reading under which the number means anything.
   */
  private applyInfrastructure(actualInfra: number, houses: HouseManager, roads: boolean): void {
    if (!roads) return;
    const builtCount = houses.getBuiltCount();
    const maintenanceCost = 20 + builtCount * 3;
    // quality += (actualInfra / maintenanceCost) * 10 - 3
    const gain = maintenanceCost > 0 ? (actualInfra / maintenanceCost) * 10 : 0;
    this.roadQuality = Math.max(0, Math.min(100, this.roadQuality + gain - 3));
    this.totalInfraSpending += actualInfra;
    this.governmentPool -= actualInfra;

    // News for very low road quality
    if (this.roadQuality < 20) {
      houses.addNews('Roads deteriorating — cars slowing down', 'economy');
    }
  }

  /**
   * Distribute dividends from post-tax corporate revenue to shareholders.
   */
  private distributeDividends(houses: HouseManager): void {
    const allHouses = houses.getHouses();
    for (const house of allHouses) {
      if (house.occupant !== 'corporation') continue;
      const revenue = houses.getHouseRevenue(house.id);
      if (revenue <= 0) continue;

      const dividendPool = revenue * this.config.DIVIDEND_RATE;
      if (dividendPool <= 0) continue;

      const info = houses.getHouseShareholderInfo(house.id);
      if (info.totalShares <= 0) continue;

      // Shareholder ids are sorted rather than taken in `Object.entries` order.
      // Numeric-string keys happen to come back ascending from V8, which made
      // this deterministic for a reason nobody had written down and which no
      // part of the language promises (`docs/determinism.md` rule 4). Payment
      // order matters here because each payment is a float addition.
      for (const familyId of sortedNumericKeys(info.familyShares)) {
        const amount = dividendPool * (info.familyShares[familyId] / info.totalShares);
        houses.addFamilyCash(familyId, amount);
      }

      // Distribute to corporate shareholders (adds to their revenue, taxed next cycle)
      for (const corpId of sortedNumericKeys(info.corpShares)) {
        const amount = dividendPool * (info.corpShares[corpId] / info.totalShares);
        houses.addHouseRevenue(corpId, amount);
      }

      houses.deductHouseRevenue(house.id, dividendPool);
      houses.addHouseDividendsPaid(house.id, dividendPool);
      this.totalDividendsDistributed += dividendPool;
    }
  }

  /**
   * Families with extra cash randomly buy shares.
   */
  private familySharePurchases(houses: HouseManager): void {
    const families = houses.getAllFamilies();
    const builtCorps = houses.getHouses().filter(h => h.occupant === 'corporation' && h.built);
    if (builtCorps.length === 0) return;

    const sharePrice = 20;
    for (const family of families) {
      // Both draws happen for every eligible family, whether or not the first
      // one succeeds, so one family's luck cannot shift the next family's.
      const buys = this.rng.chance(0.15);
      const corp = this.rng.pick(builtCorps);
      if (family.cash > sharePrice * 3 && buys && corp) {
        houses.addFamilyCash(family.id, -sharePrice);
        houses.grantFamilyShare(family.id, corp.id);
      }
    }
  }

  /**
   * Process recurring payments (insurance, telecom, healthcare).
   */
  private processRecurringPayments(houses: HouseManager): void {
    const families = houses.getAllFamilies();
    for (const family of families) {
      for (const industry of ALL_INDUSTRIES) {
        const config = INDUSTRY_CONFIGS[industry];
        if (config.paymentModel !== 'recurring') continue;

        const corpHouseId = family.subscriptions[industry];
        if (corpHouseId === undefined) continue;

        // Check if this corp still exists and is built
        const corpHouse = houses.getHouseById(corpHouseId);
        if (!corpHouse || !corpHouse.built) continue;

        // Accumulate timer for this industry
        const timer = houses.getRecurringTimer(family.id, industry);
        const newTimer = timer + RECURRING_CHECK_INTERVAL;

        if (newTimer >= config.recurringInterval) {
          // Payment due. Carry the overshoot so a subscription's billing period
          // stays exact instead of stretching by up to one check interval.
          houses.setRecurringTimer(family.id, industry, newTimer - config.recurringInterval);
          const cash = houses.getFamilyCash(family.id);
          if (cash >= config.paymentAmount) {
            houses.addFamilyCash(family.id, -config.paymentAmount);
            houses.addHouseRevenue(corpHouseId, config.paymentAmount);
          } else {
            this.paymentFailuresThisCycle++;
            if (!this.failureStatsMode) {
              houses.addNews(`The ${family.name}s can't afford ${config.label} this month`, 'economy');
            }
          }
        } else {
          houses.setRecurringTimer(family.id, industry, newTimer);
        }
      }
    }
  }

  /**
   * Process a visit payment (at-visit industries). Called when a family is dropped off.
   */
  processVisitPayment(familyId: number, houseId: number, houses: HouseManager): void {
    const house = houses.getHouseById(houseId);
    if (!house || house.occupant !== 'corporation') return;

    const industryType = houses.getHouseIndustry(houseId);
    if (!industryType) return;

    const config = INDUSTRY_CONFIGS[industryType];
    if (config.paymentModel !== 'at_visit') return;

    const cash = houses.getFamilyCash(familyId);
    if (cash >= config.paymentAmount) {
      houses.addFamilyCash(familyId, -config.paymentAmount);
      houses.addHouseRevenue(houseId, config.paymentAmount);
    } else {
      this.paymentFailuresThisCycle++;
      if (!this.failureStatsMode) {
        const family = houses.getFamily(familyId);
        if (family) {
          houses.addNews(`The ${family.name}s went to ${config.label} but couldn't pay`, 'economy');
        }
      }
    }
  }

  /**
   * The treasury's complete state.
   *
   * Everything a *continuing* world needs, which is more than a displayed one
   * does: the generator's position, the cycle counter, the last cycle's figures,
   * the cycle timers, and how many payments have failed inside the cycle
   * currently running. A treasury restored without that last one forgives debts
   * nobody paid off.
   */
  save(): EconomySnapshot {
    return {
      governmentPool: this.governmentPool,
      totalTaxCollected: this.totalTaxCollected,
      totalLeaseCollected: this.totalLeaseCollected,
      totalTollsCollected: this.totalTollsCollected,
      totalRoadWear: this.totalRoadWear,
      totalInterchangeSpending: this.totalInterchangeSpending,
      totalUtilityBilled: this.totalUtilityBilled,
      totalTitheRemitted: this.totalTitheRemitted,
      totalGrantsReceived: this.totalGrantsReceived,
      totalWorksSpending: this.totalWorksSpending,
      totalUBIDistributed: this.totalUBIDistributed,
      totalDividendsDistributed: this.totalDividendsDistributed,
      roadQuality: this.roadQuality,
      totalBusinessGrantsPaid: this.totalBusinessGrantsPaid,
      totalInfraSpending: this.totalInfraSpending,
      totalInterestPaid: this.totalInterestPaid,
      ubiTimer: this.ubiTimer,
      recurringTimer: this.recurringTimer,
      leaseMark: this.leaseMark,
      tollMark: this.tollMark,
      lastCycleRevenue: this.lastCycleRevenue,
      lastCycleReceipts: this.lastCycleReceipts,
      lastCycleSpending: this.lastCycleSpending,
      lastCycleInterest: this.lastCycleInterest,
      cyclesSettled: this.cyclesSettled,
      paymentFailuresThisCycle: this.paymentFailuresThisCycle,
      failureStatsMode: this.failureStatsMode,
      spendingConstrained: this.spendingConstrained,
      rng: this.rng.save(),
    };
  }

  /** Restore from `save()`. */
  load(state: EconomySnapshot): void {
    this.governmentPool = state.governmentPool;
    this.totalTaxCollected = state.totalTaxCollected;
    this.totalLeaseCollected = state.totalLeaseCollected;
    this.totalTollsCollected = state.totalTollsCollected;
    this.totalRoadWear = state.totalRoadWear;
    this.totalInterchangeSpending = state.totalInterchangeSpending;
    this.totalUtilityBilled = state.totalUtilityBilled;
    this.totalTitheRemitted = state.totalTitheRemitted;
    this.totalGrantsReceived = state.totalGrantsReceived;
    this.totalWorksSpending = state.totalWorksSpending;
    this.totalUBIDistributed = state.totalUBIDistributed;
    this.totalDividendsDistributed = state.totalDividendsDistributed;
    this.roadQuality = state.roadQuality;
    this.totalBusinessGrantsPaid = state.totalBusinessGrantsPaid;
    this.totalInfraSpending = state.totalInfraSpending;
    this.totalInterestPaid = state.totalInterestPaid;
    // Restored, not zeroed. The client's old save format reset the cycle timers,
    // which handed the player a fresh budget cycle on every reload — the
    // determinism hole `IMPLEMENTATION-GOVERNORS.md` §5 item 6 names.
    this.ubiTimer = state.ubiTimer;
    this.recurringTimer = state.recurringTimer;
    // The rent already counted as receipts. A district restored with this at zero
    // spends the estate's whole rental history on one cycle of road maintenance.
    this.leaseMark = state.leaseMark;
    // And the tolls. A district restored with this at zero funds one cycle of road
    // maintenance out of every crossing that has ever been made of it.
    this.tollMark = state.tollMark;
    this.lastCycleRevenue = state.lastCycleRevenue;
    this.lastCycleReceipts = state.lastCycleReceipts;
    this.lastCycleSpending = state.lastCycleSpending;
    this.lastCycleInterest = state.lastCycleInterest;
    this.cyclesSettled = state.cyclesSettled;
    this.paymentFailuresThisCycle = state.paymentFailuresThisCycle;
    this.failureStatsMode = state.failureStatsMode;
    // And whether the ticker has already said the roads are unfunded.
    this.spendingConstrained = state.spendingConstrained;
    this.rng.load(state.rng);
    // The budget card is a view over all of the above.
    this.cachedSnapshot = null;
  }

  /**
   * Order-stable view of the fiscal state, for hashing.
   *
   * Includes the cycle timers: two treasuries holding the same money but at
   * different points in the budget cycle will diverge at the next settlement.
   */
  canonicalState(): Canonical {
    const state: Record<string, Canonical> = {
      governmentPool: this.governmentPool,
      totalTaxCollected: this.totalTaxCollected,
      totalUBIDistributed: this.totalUBIDistributed,
      totalDividendsDistributed: this.totalDividendsDistributed,
      roadQuality: this.roadQuality,
      totalBusinessGrantsPaid: this.totalBusinessGrantsPaid,
      totalInfraSpending: this.totalInfraSpending,
      totalInterestPaid: this.totalInterestPaid,
      lastCycleRevenue: this.lastCycleRevenue,
      lastCycleSpending: this.lastCycleSpending,
      lastCycleInterest: this.lastCycleInterest,
      ubiTimer: this.ubiTimer,
      recurringTimer: this.recurringTimer,
      cyclesSettled: this.cyclesSettled,
      paymentFailuresThisCycle: this.paymentFailuresThisCycle,
      failureStatsMode: this.failureStatsMode,
      rng: this.rng.save() as unknown as Canonical,
    };
    // Omitted while nothing federal has ever paid rent here, which is every
    // district in this repository's golden masters. The same move the two flows,
    // the estate and the labour market all make, and for the same reason: a
    // treasury that has never received a lease and one that cannot are the same
    // treasury.
    if (this.totalLeaseCollected > 0) state.totalLeaseCollected = this.totalLeaseCollected;
    // And omitted while the mayor has never bought anything that does not pay,
    // which is every district in those same golden masters. Two treasuries that
    // agree on every figure and disagree here have not diverged *yet* — nothing
    // reads this number — but they will the moment §8 gives the pool a
    // consequence, and a hash that could not tell them apart would be a hash that
    // let mercy in for free.
    if (this.totalWorksSpending > 0) state.totalWorksSpending = this.totalWorksSpending;
    // The three §8 lines, on the same terms as the two above. `lastCycleReceipts`
    // and `leaseMark` are in the group because they are only ever non-zero in a
    // district that has something to ration, and `spendingConstrained` because a
    // treasury that has never been short has nothing to say about it.
    if (this.totalUtilityBilled > 0) state.totalUtilityBilled = this.totalUtilityBilled;
    if (this.totalTitheRemitted > 0) state.totalTitheRemitted = this.totalTitheRemitted;
    if (this.totalGrantsReceived > 0) state.totalGrantsReceived = this.totalGrantsReceived;
    if (this.leaseMark > 0) state.leaseMark = this.leaseMark;
    if (this.lastCycleReceipts !== this.lastCycleRevenue) {
      state.lastCycleReceipts = this.lastCycleReceipts;
    }
    if (this.spendingConstrained) state.spendingConstrained = true;
    // And the corridor's three, on exactly those terms. A district off it has
    // never earned a toll, never had a truck wear its road and never bought an
    // interchange, and hashes to what it hashed to before there was freight.
    if (this.totalTollsCollected > 0) state.totalTollsCollected = this.totalTollsCollected;
    if (this.tollMark > 0) state.tollMark = this.tollMark;
    if (this.totalRoadWear > 0) state.totalRoadWear = this.totalRoadWear;
    if (this.totalInterchangeSpending > 0) {
      state.totalInterchangeSpending = this.totalInterchangeSpending;
    }
    return state;
  }

  /**
   * Get initial cash for a new family.
   */
  static getInitialFamilyCash(): number {
    return INITIAL_FAMILY_CASH;
  }
}
